import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { tagApi } from '../api/tags';
import type { Tag } from '../types';
import { useAuth } from '../context/AuthContext';
import { TagChip } from '../components/TicketTags';

// Skrzynkę mailową na powiadomienia o zgłoszeniach konfiguruje każdy integrator
// u siebie (Pracownicy → Powiadomienia mailowe), nie super-admin.
const SettingsPage: React.FC = () => {
  const { user } = useAuth();

  if (!user?.roles?.includes('ADMIN')) {
    return <Navigate to="/machines" replace />;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Ustawienia aplikacji</h1>
      </div>

      <TagsAdminCard />
    </div>
  );
};

const TAG_COLOR = '#1e78d6';

const TagsAdminCard: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await tagApi.list();
      setTags(data);
    } catch (e: any) {
      setError('Nie udało się załadować tagów');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await tagApi.create({ name: name.trim(), color: TAG_COLOR });
      setName('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się dodać tagu');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tag: Tag) => {
    if (!window.confirm(`Usunąć tag "${tag.name}"? Zostanie usunięty ze wszystkich zgłoszeń.`)) return;
    try {
      await tagApi.delete(tag.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się usunąć tagu');
    }
  };

  return (
    <div className="card" style={{ padding: '1.5rem', maxWidth: 640, marginTop: '1.5rem' }}>
      <h2 style={{ fontSize: '1.0625rem', marginTop: 0, marginBottom: '0.5rem' }}>Tagi zgłoszeń</h2>
      <p className="item-meta" style={{ marginBottom: '1rem', fontSize: '0.875rem' }}>
        Lista tagów dostępna integratorom przy edycji zgłoszeń. Tagi pomagają porządkować i wyszukiwać tickety
        (np. <em>Elektryka</em>, <em>PLC</em>, <em>Dokumentacja</em>).
      </p>

      {error && <div className="auth-error">{error}</div>}

      {loading ? (
        <div className="empty-state">Ładowanie…</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
            {tags.length === 0 && <span className="item-meta">Brak tagów</span>}
            {tags.map((t) => (
              <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <TagChip tag={{ ...t, color: TAG_COLOR }} />
                <button
                  type="button"
                  onClick={() => remove(t)}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: 'var(--color-danger)', fontSize: '0.75rem', padding: '0 0.25rem',
                  }}
                  title="Usuń tag"
                >
                  usuń
                </button>
              </span>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="form-input"
              type="text"
              placeholder="Nazwa tagu"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ flex: 1, minWidth: 180 }}
              maxLength={60}
            />
            <button className="btn btn-primary btn-sm" onClick={create} disabled={saving || !name.trim()}>
              {saving ? '…' : 'Dodaj'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SettingsPage;
